import React from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { SHORTAGE_ALLOWANCE_MODES, shortageAllowanceModeMeta } from "../lib/freightSettlement";
import { formatInr, formatQtl } from "../lib/accountsSettlement";

/**
 * Sales freight shortage — mill WB vs party WB, with same allowance modes as purchase.
 */
export default function SalesFreightShortageFields({
  freight,
  setFreight,
  settlement,
  canEdit = true,
}) {
  const mode = freight?.shortage_allowance_mode || "";
  const meta = shortageAllowanceModeMeta(mode);
  const mill = settlement?.mill_net_qtl ?? settlement?.our_net_qtl;
  const party = settlement?.party_net_qtl;
  const auto = Boolean(settlement?.shortage_method === "mill_vs_party" || (mill > 0 && party > 0));
  const shortageQtl = settlement?.shortage_qtl ?? settlement?.weight_diff_qtl;
  const allowanceQtl = settlement?.allowance_qtl;
  const netShortage = settlement?.net_shortage_qtl ?? settlement?.weight_short_qtl;
  const deduction = settlement?.shortage_deduction ?? settlement?.shortage_amount;
  const goodsRate = settlement?.shortage_goods_rate_per_qtl;
  const showBreakdown = Boolean(
    auto
    || (shortageQtl != null && Number(shortageQtl) > 0)
    || (allowanceQtl != null && Number(allowanceQtl) > 0)
    || (deduction != null && Number(deduction) > 0),
  );

  return (
    <div className="rounded-md border border-orange-200 bg-orange-50/40 p-4 space-y-3 col-span-2 sm:col-span-3">
      <p className="text-xs font-medium uppercase tracking-wide text-orange-950">
        Truck shortage allowance
      </p>
      {auto ? (
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          Shortage = our weighbridge ({formatQtl(mill)}) − party weighbridge ({formatQtl(party)}).
          Tolerance before short-goods is deducted from freight — same options as purchase freight voucher.
        </p>
      ) : (
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          Enter party side weight (step 1) to auto-calculate shortage from mill vs party weighbridge.
          Allowance still reduces any shortage qtl already on the freight slip (e.g. gm per bag × bags loaded).
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Allowance basis</Label>
          <select
            className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-2 text-xs"
            value={mode || "_none"}
            disabled={!canEdit}
            onChange={(e) => {
              const v = e.target.value === "_none" ? "" : e.target.value;
              setFreight((f) => ({
                ...f,
                shortage_allowance_mode: v,
                shortage_allowance_value: v ? f.shortage_allowance_value : "",
              }));
            }}
          >
            <option value="_none">None</option>
            {SHORTAGE_ALLOWANCE_MODES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        {mode ? (
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Allowance ({meta?.unit || ""})
            </Label>
            <Input
              type="number"
              step={mode === "GM_PER_BAG" ? "1" : "0.01"}
              min="0"
              className="mt-1 h-9"
              disabled={!canEdit}
              placeholder={meta?.placeholder}
              value={freight?.shortage_allowance_value ?? ""}
              onChange={(e) => setFreight((f) => ({ ...f, shortage_allowance_value: e.target.value }))}
            />
          </div>
        ) : null}
      </div>

      {showBreakdown ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Gross shortage</p>
            <p className="font-medium tabular">{shortageQtl != null ? formatQtl(shortageQtl) : "—"}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Allowed</p>
            <p className="font-medium tabular">{allowanceQtl != null ? formatQtl(allowanceQtl) : "—"}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Net shortage</p>
            <p className="font-medium tabular">{netShortage != null ? formatQtl(netShortage) : "—"}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Deduction</p>
            <p className="font-medium tabular">
              {formatInr(deduction)}
              {goodsRate ? (
                <span className="block text-[10px] text-muted-foreground font-normal">
                  @ {formatInr(goodsRate)}/qtl
                </span>
              ) : null}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
